import { EMPTY_STATE_IMAGES } from '@shared/constants';
import { isPast, parseISO } from 'date-fns';
import uniqBy from 'lodash/uniqBy';
import { Column } from 'src/features/kanban/component/Column/types';
import { TaskT } from 'src/features/tasks/types/tasks';

export const getIncompleteTasks = (columns: Column[]) =>
  columns.map((c) => c.items.filter((i) => !i.completed).length).reduce((a, c) => (a += c), 0);

export const getCompletedTasks = (columns: Column[]) =>
  columns.map((c) => c.items.filter((i) => i.completed).length).reduce((a, c) => (a += c), 0);

export const getTotalTasks = (columns: Column[]) =>
  columns.map((c) => c.items.length).reduce((a, c) => (a += c), 0);

export const getOverDueTasks = (columns: Column[]) => {
  const isTaskOverDue = (item: TaskT) => {
    if (item.completed) return false;
    if (item.assignDate?.length === 1 && item.assignDate?.[0]) {
      const endDate = parseISO(item.assignDate[0]);
      if (isPast(endDate)) {
        return true;
      }
    }
    if (item.assignDate?.length === 2 && item.assignDate?.[0] && item.assignDate?.[1]) {
      const endDate = parseISO(item.assignDate[1]);
      if (isPast(endDate)) {
        return true;
      }
    }
    return false;
  };

  return columns
    .map((c) => c.items.filter((i) => isTaskOverDue(i)).length)
    .reduce((a, c) => (a += c), 0);
};

export const unassignedUser = {
  firstname: 'Unassigned',
  avatar: EMPTY_STATE_IMAGES.avatar,
  memberId: '',
  lastname: '',
  email: '',
};

export const commonGridAxisValues = [0, 2, 5, 10, 20, 50, 70, 100];

export const groupTasksByAssignees = (data: Column[]) => {
  const allTaskAssignees = data.map((c) => c.items.map((i) => i.assignee).flat()).flat();
  const allTasks = data.map((c) => c.items.flat()).flat();
  const uniqueAssignees = uniqBy(allTaskAssignees, 'memberId');

  const groupedTasks = uniqueAssignees.map((member) => ({
    assignee: member,
    tasks: allTasks.filter((task) =>
      task.assignee?.some((assignee) => assignee.memberId === member?.memberId)
    ),
  }));

  const unassignedTasks = {
    assignee: unassignedUser,
    tasks: allTasks.filter((task) => (task.assignee?.length || 0) < 1),
  };

  return [...groupedTasks, unassignedTasks];
};

export const getOverviewIncompleteTasks = (data: Column[]) => {
  const allTasks = data
    .map((c) => c.items.map((i) => (i && i.subtasks ? [i, ...i.subtasks] : [i])))
    .flat()
    .flat();
  return allTasks.filter((i) => !i?.completed).length;
};

export const getOverviewCompletedTasks = (data: Column[]) => {
  const allTasks = data
    .map((c) => c.items.map((i) => (i && i.subtasks ? [i, ...i.subtasks] : [i])))
    .flat()
    .flat();
  return allTasks.filter((i) => i?.completed).length;
};

export const getOverviewOverdueTasks = (data: Column[]) => {
  const allTasks = data
    .map((c) => c.items.map((i) => (i && i.subtasks ? [i, ...i.subtasks] : [i])))
    .flat()
    .flat();

  const overDueTasks = () =>
    allTasks.filter((task) => {
      task.endDate && isPast(parseISO(task.endDate));
    });
  return overDueTasks.length;
};

export const groupTaskCountByStatus = (data: Column[]): { string?: number } => {
  const retval: { string?: number } = {};
  const allTasks = data
    .map((c) => c.items.map((i) => (i && i.subtasks ? [i, ...i.subtasks] : [i])))
    .flat()
    .flat();
  allTasks.map((c) => {
    if (c.column) {
      if (retval[c.column]) {
        retval[c.column] = retval[c.column] + 1;
      } else {
        retval[c.column] = 1;
      }
    }
  });

  return retval;
};

export const theme = [
  '#254DDA',
  '#FF9700',
  '#A650ED',
  '#FF4A7B',
  '#FFDA47',
  '#F87463',
  '#3DD456',
  '#878A92',
  '#142A76',
  '#C27300',
  '#8240AF',
  '#EF295E',
  '#FFD321',
  '#F4381F',
  '#26AB3C',
];

export const darken = (color: string) => {
  const darkerColorDict = {
    '#F6F7F9': '#c6d6f5',
    '#DFF8E3': '#73d984',
    '#FFF7D6': '#e3c759',
    '#FFE6BF': '#dea243',
    '#FEEFED': '#e37566',
    '#FEDEE6': '#d96a86',
    '#F1E2FF': '#9d64d1',
    '#EEF1FC': '#879ae0',
  };
  if (Object.keys(darkerColorDict).includes(color)) {
    return darkerColorDict[color];
  } else {
    return color;
  }
};

export const calculateChartWidth = (listnum: number) => {
  if (listnum > 10) {
    return `${500 + (listnum - 10) * 32}px`;
  }
  return '450px';
};
